// Add to a draft PO from anywhere items are listed (Mitch, 2026-11-03: "if we see an item is out of stock, and we already
// have a draft PO for that vendor, we can just add that to an existing draft"): the items picked, by their preferred
// vendor, each vendor's lines onto its newest open draft (or one picked, or a new draft), the quantity the plan
// suggests (else the vendor's minimum). An item already on that draft has its line raised instead of a second line.
import { FilePlus2 } from "lucide-react";
import { useState } from "react";
import type { Item, Po, PoLine, Vendor, VendorItem } from "../../../../shared/db.gen.ts";
import { validQty } from "../../../../shared/po.ts";
import { qty as fmtQty } from "../../lib/format.ts";
import { mutate } from "../../lib/mutate.ts";
import { usePlan } from "../../lib/planning.ts";
import { store, useRows } from "../../lib/sync.ts";
import { Button, Input, Select } from "../../ui/controls.tsx";
import { PaneLink } from "../../ui/SidePane.tsx";
import { toast } from "../../ui/toast.tsx";
import { addLines, createPo, parseNumber, poPath, vendorLabel } from "./lib.ts";

/** A vendor's purchase drafts that haven't gone to it yet, newest first. */
const openDrafts = (pos: readonly Po[], vendorId: string) =>
  pos
    .filter((p) => p.vendor_id === vendorId && p.status === "draft" && p.kind === "purchase" && p.sent_revision === null)
    .sort((a, b) => b.created_at.localeCompare(a.created_at));

/** The box under an "Add to PO…" button: `items` picked, `close` when done. */
export function AddToPo({ items, close, onDone }: { items: Item[]; close: () => void; onDone?: () => void }) {
  const links = useRows("vendor_item");
  const vendors = useRows("vendor");
  const pos = useRows("po");
  const plan = usePlan();
  const linkOf = (i: Item): VendorItem | undefined => {
    const mine = links.filter((l) => l.item_id === i.id);
    return mine.find((l) => l.is_preferred) ?? mine[0];
  };
  const groups = new Map<string, { vendor: Vendor; rows: { item: Item; link: VendorItem; suggested: number }[] }>();
  const unlinked: Item[] = [];
  for (const item of items) {
    const link = linkOf(item);
    const vendor = link && vendors.find((v) => v.id === link.vendor_id && !v.archived);
    if (!link || !vendor) {
      unlinked.push(item);
      continue;
    }
    const r = plan.byItem.get(item.id);
    const suggested = r && r.qty > 0 && r.vendor?.id === vendor.id ? r.qty : validQty(link);
    const g = groups.get(vendor.id) ?? { vendor, rows: [] };
    g.rows.push({ item, link, suggested });
    groups.set(vendor.id, g);
  }
  const [qtys, setQtys] = useState<Record<string, string>>({});
  const [targets, setTargets] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const targetOf = (vendorId: string) => targets[vendorId] ?? openDrafts(pos, vendorId)[0]?.id ?? "new";

  const add = async () => {
    const done: { id: string; number: string; created: boolean }[] = [];
    setBusy(true);
    try {
      for (const { vendor, rows } of groups.values()) {
        const lines = rows.map((r) => ({ ...r, qty: parseNumber(qtys[r.item.id] ?? String(r.suggested)) }));
        const bad = lines.find((l) => l.qty == null || l.qty <= 0);
        if (bad) return toast.error(`Enter a quantity for ${bad.item.sku || bad.item.name}`);
        const target = targetOf(vendor.id);
        if (target === "new") {
          const id = await createPo(
            vendor,
            lines.map((l) => ({ item: l.item, link: l.link, qty: l.qty as number })),
            "purchase",
            undefined,
            { open: false },
          );
          if (id) done.push({ id, number: (store.row("po", id) as Po | undefined)?.number ?? "a new draft", created: true });
          continue;
        }
        const po = pos.find((p) => p.id === target) as Po;
        const existing = (store.rows("po_line") as unknown as PoLine[]).filter((l) => l.po_id === po.id);
        // An item already on it: its line goes up (one line per item), the rest are added.
        for (const l of lines) {
          const on = existing.find((x) => x.item_id === l.item.id);
          if (on) await mutate("po_line.update", { id: on.id, patch: { qty: Number(on.qty) + (l.qty as number) } });
        }
        const fresh = lines.filter((l) => !existing.some((x) => x.item_id === l.item.id));
        if (fresh.length)
          await addLines(
            po,
            existing,
            fresh.map((l) => ({ item: l.item, link: l.link, qty: l.qty as number })),
          );
        done.push({ id: po.id, number: po.number, created: false });
      }
    } finally {
      setBusy(false);
    }
    if (done.length) {
      toast.ok(done.map((d) => `${d.created ? "Started" : "Added to"} ${d.number}`).join(" · "));
      onDone?.();
      close();
    }
  };

  return (
    <div className="flex max-h-[70vh] flex-col gap-3 overflow-y-auto">
      <div className="font-medium">Add {items.length === 1 ? items[0].sku || items[0].name : `${items.length} items`} to a draft PO</div>
      {[...groups.values()].map(({ vendor, rows }) => {
        const drafts = openDrafts(pos, vendor.id);
        const target = targetOf(vendor.id);
        return (
          <div key={vendor.id} className="rounded-md border border-line">
            <div className="flex items-center gap-2 border-b border-line bg-surface-2/50 px-2 py-1.5">
              <span className="min-w-0 flex-1 truncate font-medium">{vendorLabel(vendor)}</span>
              <span className="w-52">
                <Select
                  aria-label={`Which PO for ${vendorLabel(vendor)}`}
                  value={target}
                  onChange={(e) => setTargets({ ...targets, [vendor.id]: e.target.value })}
                  options={[...drafts.map((d) => ({ value: d.id, label: `${d.number} (draft)` })), { value: "new", label: "A new draft" }]}
                  className="h-7 text-[12px]"
                />
              </span>
              {target !== "new" && (
                <PaneLink kind="po" id={target} to={poPath(target)} className="text-[12px] text-accent-text hover:underline">
                  Open
                </PaneLink>
              )}
            </div>
            {rows.map(({ item, link, suggested }) => (
              <div key={item.id} className="flex items-center gap-2 px-2 py-1">
                <span className="w-32 shrink-0 truncate font-mono text-[12px]">{item.sku}</span>
                <span className="min-w-0 flex-1 truncate text-[12px] text-muted" title={item.name}>
                  {item.name}
                </span>
                <span className="text-[11px] text-faint" title={link.moq ? `MOQ ${fmtQty(link.moq)}` : undefined}>
                  {plan.byItem.get(item.id)?.qty ? "the plan's" : link.moq ? "MOQ" : ""}
                </span>
                <Input
                  aria-label={`How many ${item.sku}`}
                  value={qtys[item.id] ?? String(suggested)}
                  onChange={(e) => setQtys({ ...qtys, [item.id]: e.target.value })}
                  className="h-7 w-20 text-right font-mono text-[12px]"
                />
              </div>
            ))}
          </div>
        );
      })}
      {unlinked.length > 0 && (
        <div className="text-[12px] text-warn">
          No vendor linked, so not added: {unlinked.map((i) => i.sku || i.name).join(", ")}. Link a vendor in the item's panel first.
        </div>
      )}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={close}>
          Cancel
        </Button>
        <Button size="sm" variant="primary" icon={<FilePlus2 className="size-3.5" />} disabled={!groups.size || busy} onClick={() => void add()}>
          Add
        </Button>
      </div>
    </div>
  );
}
